import { useEffect, useMemo, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { tokenStore } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { iso, parseIso, MOIS, fmtMinutes, decimalHours } from "@/lib/date";
import { Download, ShieldCheck, Pencil, Lock, Users, ChevronDown, Calendar as CalIcon } from "lucide-react";

// Palette de fonds par étudiant (suffisamment de variantes claires sur thème crème)
const STUDENT_COLORS = [
  { bg: "bg-[#F5FAF8]", head: "bg-[#FFE8C7]", border: "border-[#F5D7A0]" },
  { bg: "bg-[#F0E8DC]", head: "bg-[#E3D5C0]", border: "border-[#D0BFA5]" },
  { bg: "bg-[#FBEDE3]", head: "bg-[#F4D7C5]", border: "border-[#E8C0A2]" },
  { bg: "bg-[#E8F0E0]", head: "bg-[#D2E3C2]", border: "border-[#BBD3A2]" },
  { bg: "bg-[#F8E8E8]", head: "bg-[#F0CFCF]", border: "border-[#DEAFAF]" },
  { bg: "bg-[#E8E8F0]", head: "bg-[#D2D2E0]", border: "border-[#B0B0C5]" },
  { bg: "bg-[#F0E0EC]", head: "bg-[#E3C5DA]", border: "border-[#C99CBA]" },
];
const colorFor = (idx) => STUDENT_COLORS[idx % STUDENT_COLORS.length];

function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function lastOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export default function HeuresMonthly() {
  const today = new Date();
  const [start, setStart] = useState(iso(firstOfMonth(today)));
  const [end, setEnd] = useState(iso(lastOfMonth(today)));
  const [allStudents, setAllStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // [] = tous
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Charge la liste complète d'étudiants une fois
  useEffect(() => {
    api.get("/users").then((r) => {
      const students = r.data.filter((u) => u.role === "student" && u.active);
      setAllStudents(students);
    });
  }, []);

  const load = async () => {
    try {
      const params = { start, end };
      if (selectedIds.length > 0) params.student_ids = selectedIds.join(",");
      const { data } = await api.get("/hours/grid", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, selectedIds]);

  const setCurrentMonth = () => {
    const t = new Date();
    setStart(iso(firstOfMonth(t)));
    setEnd(iso(lastOfMonth(t)));
  };
  const setPrevMonth = () => {
    const s = parseIso(start);
    const t = new Date(s.getFullYear(), s.getMonth() - 1, 1);
    setStart(iso(firstOfMonth(t)));
    setEnd(iso(lastOfMonth(t)));
  };
  const setNextMonth = () => {
    const s = parseIso(start);
    const t = new Date(s.getFullYear(), s.getMonth() + 1, 1);
    setStart(iso(firstOfMonth(t)));
    setEnd(iso(lastOfMonth(t)));
  };

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ start, end });
      if (selectedIds.length > 0) params.set("student_ids", selectedIds.join(","));
      const url = `${API}/hours/export-range?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Erreur export");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `horaires_${start}_${end}_pecan.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const validateMonth = async () => {
    const ok = window.confirm(
      `Verrouiller toutes les heures encodées entre le ${start} et le ${end}${selectedIds.length === 1 ? " pour l'étudiant sélectionné" : selectedIds.length > 1 ? " pour les étudiants sélectionnés" : ""} ?\n\n` +
      `Une fois verrouillées, les étudiants ne pourront plus modifier ces heures. ` +
      `Tu pourras les déverrouiller ou les corriger toi-même quand tu veux.`
    );
    if (!ok) return;
    try {
      const body = { start, end, validate: true };
      if (selectedIds.length === 1) body.student_id = selectedIds[0];
      const { data } = await api.post("/hours/validate", body);
      if (data.candidates === 0) {
        toast.info("Aucune heure encodée à valider sur cette plage.");
      } else {
        toast.success(`${data.updated} journée(s) verrouillée(s) sur ${data.candidates} trouvée(s)`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditCell = (student_id, date, cell) => {
    if (cell?.validated) {
      toast.info("Journée validée — déverrouille d'abord");
      return;
    }
    setEditing({
      student_id, date,
      arr: cell?.arr || "",
      dep: cell?.dep || "",
      pause_minutes: cell?.pause_min ?? 30,
    });
    setDialogOpen(true);
  };

  const saveEdit = async () => {
    try {
      await api.post("/hours/admin", {
        student_id: editing.student_id,
        date: editing.date,
        arr: editing.arr || null,
        dep: editing.dep || null,
        pause_minutes: Number(editing.pause_minutes) || 0,
      });
      toast.success("Mis à jour");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStudent = (id) => {
    setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };
  const selectAll = () => setSelectedIds([]);
  const selectNone = () => setSelectedIds(allStudents.map((u) => u.id));  // sélection inverse "aucun"

  const selectionLabel = selectedIds.length === 0
    ? `Tous (${allStudents.length})`
    : `${selectedIds.length} sélectionné${selectedIds.length > 1 ? "s" : ""}`;

  return (
    <div className="space-y-6" data-testid="page-heures-monthly">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Heures équipe</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
            Du {start} au {end}
          </h1>
          {data && (
            <p className="text-[#6b7f79] mt-1">
              Total équipe : <span className="font-bold text-[#2C4C3B]">{data.grand_total_hhmm}</span> ({decimalHours(data.grand_total_min)} h) ·
              moy. <b>{data.avg_hhmm_per_open_day}</b>/jour ouvré
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Button onClick={validateMonth} variant="outline" data-testid="validate-month-button"
                  title="Verrouille les heures encodées sur la plage choisie. Les étudiants ne pourront plus les modifier."
                  className="rounded-full border-[#388E3C] text-[#388E3C] hover:bg-[#E8F5E9] h-11">
            <ShieldCheck className="w-4 h-4 mr-2" /> Verrouiller la plage
          </Button>
          <Button onClick={exportXlsx} disabled={busy} data-testid="export-xlsx-button" className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] shadow-md h-11">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <Card className="card-soft border-0">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="label-tiny">Du</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 rounded-xl border-[#DDE7E4]" data-testid="heures-start" />
          </div>
          <div>
            <Label className="label-tiny">Au</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 rounded-xl border-[#DDE7E4]" data-testid="heures-end" />
          </div>
          <div className="flex gap-1">
            <Button onClick={setPrevMonth} variant="outline" size="sm" data-testid="heures-month-prev" className="rounded-full border-[#DDE7E4] text-[#6b7f79] h-10"><CalIcon className="w-3 h-3 mr-1" /> Mois préc.</Button>
            <Button onClick={setCurrentMonth} variant="outline" size="sm" data-testid="heures-month-today" className="rounded-full border-[#DDE7E4] text-[#6b7f79] h-10">Mois courant</Button>
            <Button onClick={setNextMonth} variant="outline" size="sm" data-testid="heures-month-next" className="rounded-full border-[#DDE7E4] text-[#6b7f79] h-10">Mois suiv.</Button>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="label-tiny">Étudiants</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="student-selector" className="mt-1 h-10 w-full justify-between rounded-xl border-[#DDE7E4] text-[#1A2B27]">
                  <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#94ADA7]" /> {selectionLabel}</span>
                  <ChevronDown className="w-4 h-4 text-[#94ADA7]" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[280px] p-0" data-testid="student-selector-popover">
                <div className="flex justify-between p-2 border-b border-[#DDE7E4]">
                  <button onClick={selectAll} data-testid="select-all-students" className="text-xs font-semibold text-[#2C4C3B] hover:underline">Tous</button>
                  <button onClick={() => setSelectedIds([])} className="text-xs font-semibold text-[#6b7f79] hover:underline">Réinitialiser</button>
                </div>
                <div className="max-h-[300px] overflow-auto p-2 space-y-1">
                  {allStudents.map((u, idx) => {
                    const col = colorFor(idx);
                    const isSelected = selectedIds.length === 0 || selectedIds.includes(u.id);
                    return (
                      <label key={u.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-[#F1EDE3]`} data-testid={`student-opt-${u.id}`}>
                        <Checkbox
                          checked={selectedIds.length === 0 ? false : selectedIds.includes(u.id)}
                          onCheckedChange={() => toggleStudent(u.id)}
                        />
                        <span className={`w-3 h-3 rounded-full ${col.head}`}></span>
                        <span className="text-sm text-[#1A2B27] flex-1">{u.name}</span>
                      </label>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.student_totals.map((s, idx) => {
              const col = colorFor(idx);
              return (
                <Card key={s.id} className={`card-soft border-0 ${col.bg}`} data-testid={`student-total-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="text-[#1A2B27] font-semibold">{s.name}</div>
                    <div className="font-display text-2xl text-[#2C4C3B] mt-1">{s.total_hhmm}</div>
                    <div className="text-xs text-[#94ADA7]">{s.days_worked} j · {s.total_hours} h</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="card-soft border-0 overflow-hidden">
            <CardContent className="p-0 overflow-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-[#F1EDE3]">
                    <th rowSpan={2} className="sticky left-0 z-10 bg-[#F1EDE3] text-left p-2 font-semibold text-[#1A2B27] min-w-[80px]">Jour</th>
                    <th rowSpan={2} className="sticky left-[80px] z-10 bg-[#F1EDE3] text-center p-2 font-semibold text-[#1A2B27] min-w-[36px]">#</th>
                    {data.students.map((st, idx) => {
                      const col = colorFor(idx);
                      return (
                        <th key={st.id} colSpan={5} className={`text-center p-1 font-semibold text-[#1A2B27] border-l border-[#DDE7E4] ${col.head}`} data-testid={`th-${st.id}`}>
                          {st.name}
                        </th>
                      );
                    })}
                  </tr>
                  <tr className="bg-[#F7F9F8]">
                    {data.students.flatMap((st, idx) => {
                      const col = colorFor(idx);
                      return ["ARR", "DEP", "Total", "Pause", "Presté"].map((h, i) => (
                        <th key={`${st.id}-${i}`} className={`text-center p-1 font-medium text-[#94ADA7] border-l border-[#DDE7E4] ${col.bg}`}>{h}</th>
                      ));
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((d) => {
                    const dd = parseIso(d.date);
                    const closedBg = d.is_closed ? "bg-[#F1EDE3] text-[#B8C7C2]" : "";
                    return (
                      <tr key={d.date} className={`border-t border-[#DDE7E4] ${closedBg}`}>
                        <td className={`sticky left-0 z-10 ${d.is_closed ? "bg-[#F1EDE3]" : "bg-white"} p-1.5 font-medium text-[#1A2B27]`}>
                          <div className="text-[10px] uppercase">{["L", "M", "M", "J", "V", "S", "D"][d.dow]}</div>
                          <div>{dd.getDate()}/{String(dd.getMonth() + 1).padStart(2, "0")}</div>
                          {d.is_holiday && <div className="text-[9px] text-[#3C6750]">{d.holiday_name?.slice(0, 14)}</div>}
                        </td>
                        <td className={`sticky left-[80px] z-10 ${d.is_closed ? "bg-[#F1EDE3]" : "bg-white"} text-center p-1.5 font-bold text-[#3C6750]`}>
                          {d.present_count || ""}
                        </td>
                        {data.students.map((st, idx) => {
                          const col = colorFor(idx);
                          const cell = data.grid[st.id]?.[d.date] || {};
                          const validated = cell.validated;
                          const empty = !cell.arr && !cell.dep;
                          return (
                            <td
                              key={`${st.id}-${d.date}`}
                              colSpan={5}
                              className={`p-0 border-l border-[#DDE7E4] ${d.is_closed ? "" : `cursor-pointer hover:brightness-95 ${col.bg}`}`}
                              onClick={() => !d.is_closed && openEditCell(st.id, d.date, cell)}
                              data-testid={`monthly-cell-${st.id}-${d.date}`}
                            >
                              <div className="grid grid-cols-5">
                                <div className="grid-cell">{cell.arr || (empty ? "" : "—")}</div>
                                <div className="grid-cell">{cell.dep || ""}</div>
                                <div className="grid-cell">{cell.total_h || ""}</div>
                                <div className="grid-cell">{cell.pause || ""}</div>
                                <div className={`grid-cell font-semibold ${validated ? "bg-[#E8F5E9] text-[#2E7D32]" : "text-[#3C6750]"}`}>
                                  {cell.total_preste || ""}{validated && <Lock className="w-2.5 h-2.5 inline ml-0.5" />}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-[#2C4C3B] bg-[#C9DBD6] font-bold">
                    <td className="sticky left-0 z-10 bg-[#C9DBD6] p-2 text-[#1A2B27]">TOTAL</td>
                    <td className="sticky left-[80px] z-10 bg-[#C9DBD6] p-2 text-center text-[#1A2B27]">{data.grand_total_days}</td>
                    {data.student_totals.map((s, idx) => {
                      const col = colorFor(idx);
                      return (
                        <td key={s.id} colSpan={5} className={`text-center p-2 border-l border-[#DDE7E4] text-[#3C6750] ${col.head}`}>
                          {s.total_hhmm} <span className="font-normal text-xs">({s.total_hours} h)</span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="monthly-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">Modifier les heures</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <p className="text-sm text-[#6b7f79]">{editing.date}</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label-tiny">Arrivée</label>
                  <Input type="time" value={editing.arr} onChange={(e) => setEditing({ ...editing, arr: e.target.value })} data-testid="monthly-edit-arr" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                </div>
                <div>
                  <label className="label-tiny">Départ</label>
                  <Input type="time" value={editing.dep} onChange={(e) => setEditing({ ...editing, dep: e.target.value })} data-testid="monthly-edit-dep" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                </div>
                <div>
                  <label className="label-tiny">Pause (min)</label>
                  <Input type="number" min={0} max={240} value={editing.pause_minutes} onChange={(e) => setEditing({ ...editing, pause_minutes: e.target.value })} data-testid="monthly-edit-pause" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="monthly-edit-save" className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528]">
              <Pencil className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
